const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || "";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export async function requestPushPermission(): Promise<boolean> {
  if (!("Notification" in window)) return false;
  if (!("serviceWorker" in navigator)) return false;

  const permission = await Notification.requestPermission();
  return permission === "granted";
}

export async function subscribeToPush(): Promise<PushSubscription | null> {
  if (!VAPID_PUBLIC_KEY) {
    console.warn("[push] No VAPID_PUBLIC_KEY configured");
    return null;
  }

  try {
    console.log("[push] Waiting for service worker ready...");
    // On iOS, navigator.serviceWorker.ready can hang after the permission dialog.
    // Use a timeout to avoid blocking the UI indefinitely.
    const registration = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error("Service worker ready timeout")),
          10_000
        )
      ),
    ]);

    // Always call pushManager.subscribe() instead of checking getSubscription()
    // first. This is idempotent per the Web Push spec — returns the existing
    // subscription if still valid, or creates a new one if the old one expired.
    // Critical for iOS Safari where subscriptions silently expire after ~1-2 weeks.
    console.log("[push] Subscribing (idempotent)...");
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(
        VAPID_PUBLIC_KEY
      ) as unknown as BufferSource,
    });
    console.log(
      "[push] Subscription active:",
      subscription.endpoint.slice(0, 60)
    );
    return subscription;
  } catch (error) {
    console.error("[push] Failed to subscribe:", error);
    return null;
  }
}

export async function getExistingSubscription(): Promise<PushSubscription | null> {
  try {
    const registration = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error("Service worker ready timeout")),
          10_000
        )
      ),
    ]);
    return registration.pushManager.getSubscription();
  } catch {
    return null;
  }
}

export function isPushSupported(): boolean {
  return (
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

/** Returns the current notification permission without prompting. */
export function getNotificationPermission(): NotificationPermission | null {
  if (!("Notification" in window)) return null;
  return Notification.permission;
}

/** Unsubscribe from push at the browser level. */
export async function unsubscribeFromPush(): Promise<boolean> {
  try {
    const subscription = await getExistingSubscription();
    if (subscription) {
      return subscription.unsubscribe();
    }
    return true;
  } catch {
    return false;
  }
}
